import { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Flex,
  Icon,
} from '@kognitos/lattice';
import { TriggerStatusBadge } from '../TriggerTypeSelector/TriggerStatusBadge';
import { DialogStepper } from '../WebhookTriggerDialog/DialogStepper';
import { ConfigureStep } from './ConfigureStep';
import { POLL_INTERVAL_MIN, POLL_INTERVAL_MAX } from './constants';
import { MapInputsStep } from '../WebhookTriggerDialog/MapInputsStep';
import type { IPollTriggerDialogProps, IStepConfig } from './types';
import {
  getPrimaryButtonLabel,
  getPrimaryButtonVariant,
  getDialogTitle,
} from '../WebhookTriggerDialog/utils';
import { TRIGGER_STATUS } from '../TriggerTypeSelector/TriggerType';
import { IntegrationAssistantPanel } from '@/modules/integrations/components/IntegrationAssistantPanel';
import {
  TriggerAssistantDialogLayout,
  TRIGGER_DIALOG_MAX_WIDTH_CLASS,
} from '../shared/TriggerAssistantDialogLayout';
import { useTriggerAssistant } from '../shared/useTriggerAssistant';
import {
  getConnectionAction,
  useInactiveConnectionGate,
} from '../../hooks/use-inactive-connection-gate';

/**
 * PollTriggerDialog Component
 *
 * A multi-step dialog for configuring poll triggers.
 * Supports both 'create' mode for new triggers and 'edit' mode for existing triggers.
 *
 * Contains two steps:
 * 1. Configure - Select integration, connection, event and interval
 * 2. Map Inputs - Map poll payload to automation inputs
 *
 * In edit mode:
 * - Pre-fills form with existing trigger data
 * - Shows dynamic button labels based on whether changes were made
 * - Displays "Pause Trigger" button in footer
 * - Shows Active/Paused badge in header
 *
 * @param props - Component props
 * @returns JSX element containing the dialog
 */
export function PollTriggerDialog({
  isOpen,
  onOpenChange,
  inputSpecs,
  isLoading = false,
  onSave,
  mode = 'create',
  initialData,
  triggerName,
  triggerStatus,
  onUpdate,
  onToggleActiveState,
  isToggleActiveStateLoading,
  // Controlled state props
  currentStep,
  onStepChange,
  selectedIntegration,
  onSelectedIntegrationChange,
  selectedIntegrationVersion,
  onSelectedIntegrationVersionChange,
  selectedConnection,
  onSelectedConnectionChange,
  selectedPollId,
  onSelectedPollChange,
  inputMappings,
  onInputMappingsChange,
  selectedPollIntervalMinutes,
  onSelectedPollIntervalMinutesChange,
  configurationValues,
  onConfigurationValuesChange,
  triggerFieldGroups,
  onTriggerFieldGroupsChange,
}: IPollTriggerDialogProps) {
  const { t } = useTranslation('automation-inputs');

  // Shared assistant wiring (gate + room + canAssist, org/workspace ids, the
  // integration display-name state, and the config-form highlight). Poll has no
  // filters, so there's no second highlight here.
  const {
    canAssist,
    organizationId,
    workspaceId,
    selectedIntegrationDisplayName,
    setSelectedIntegrationDisplayName,
    configFill,
  } = useTriggerAssistant(
    selectedIntegration,
    selectedIntegrationVersion,
    selectedConnection,
    selectedPollId,
  );

  // The config field keys the user can actually fill, reported up from
  // ConfigureStep. Used to reject a set_trigger_field call from the assistant
  // for any key that isn't a visible, fillable field.
  const [fillableConfigKeys, setFillableConfigKeys] = useState<string[]>([]);

  // Check if inputSpecs has any items
  const hasInputSpecs = useMemo(
    () => inputSpecs && inputSpecs.length > 0,
    [inputSpecs],
  );

  /**
   * Checks if configure step values have changed from initial values
   */
  const hasConfigureChanges = useMemo(() => {
    if (mode === 'create') return false;

    return (
      selectedIntegration !== initialData?.selectedIntegration ||
      selectedIntegrationVersion !== initialData?.selectedIntegrationVersion ||
      selectedConnection !== initialData?.selectedConnection ||
      selectedPollId !== initialData?.selectedPollId ||
      selectedPollIntervalMinutes !== initialData?.selectedPollIntervalMinutes
    );
  }, [
    mode,
    selectedIntegration,
    selectedIntegrationVersion,
    selectedConnection,
    selectedPollId,
    selectedPollIntervalMinutes,
    initialData,
  ]);

  /**
   * Checks if mapping values have changed from initial values
   */
  const hasMappingChanges = useMemo(() => {
    if (mode === 'create') return false;

    const initialMappings = initialData?.inputMappings ?? {};
    const currentKeys = Object.keys(inputMappings);
    const initialKeys = Object.keys(initialMappings);

    // Check if number of mappings is different
    if (currentKeys.length !== initialKeys.length) return true;

    // Check if any mapping value is different
    return currentKeys.some(key => inputMappings[key] !== initialMappings[key]);
  }, [mode, inputMappings, initialData]);

  /**
   * Checks if configuration values have changed from initial values
   */
  const hasConfigurationChanges = useMemo(() => {
    if (mode === 'create') return false;

    const initialConfig = initialData?.configurationValues ?? {};
    const currentKeys = Object.keys(configurationValues);
    const initialKeys = Object.keys(initialConfig);

    // Check if number of configuration keys is different
    if (currentKeys.length !== initialKeys.length) return true;

    // Check if any configuration value is different
    return currentKeys.some(
      key => configurationValues[key] !== initialConfig[key],
    );
  }, [mode, configurationValues, initialData]);

  /**
   * Checks if any changes have been made across all steps
   */
  const hasAnyChanges = useMemo(() => {
    return hasConfigureChanges || hasMappingChanges || hasConfigurationChanges;
  }, [hasConfigureChanges, hasMappingChanges, hasConfigurationChanges]);

  /**
   * Checks if step 1 has all mandatory fields filled with valid values
   */
  const isStep1Valid = useMemo(() => {
    if (!selectedIntegration || !selectedConnection || !selectedPollId)
      return false;
    if (selectedPollIntervalMinutes === undefined) return false;
    return (
      selectedPollIntervalMinutes >= POLL_INTERVAL_MIN &&
      selectedPollIntervalMinutes <= POLL_INTERVAL_MAX
    );
  }, [
    selectedIntegration,
    selectedConnection,
    selectedPollId,
    selectedPollIntervalMinutes,
  ]);

  /**
   * Handles mapping change for an input
   */
  const handleMappingChange = useCallback(
    (inputName: string, triggerFieldValue: string) => {
      onInputMappingsChange({
        ...inputMappings,
        [inputName]: triggerFieldValue,
      });
    },
    [inputMappings, onInputMappingsChange],
  );

  /**
   * Handles configuration value change
   */
  const handleConfigurationChange = useCallback(
    (key: string, value: string | boolean | number) => {
      onConfigurationValuesChange({
        ...configurationValues,
        [key]: value,
      });
    },
    [configurationValues, onConfigurationValuesChange],
  );

  /**
   * Fills a configuration field on shifu's behalf (its `set_trigger_field`
   * client tool). Validates the key against the poll's fillable config schema so
   * a hallucinated or injected key can't write an arbitrary (or runtime-managed)
   * entry; returns an error string (surfaced back to shifu) for an unknown key.
   */
  const handleAssistantFillForm = useCallback(
    (field: string, value: string): string | void => {
      if (!fillableConfigKeys.includes(field)) {
        return fillableConfigKeys.length
          ? `Unknown field "${field}". Fillable fields: ${fillableConfigKeys.join(', ')}.`
          : 'This trigger has no fillable configuration fields.';
      }
      handleConfigurationChange(field, value);
      configFill.mark(field);
    },
    [fillableConfigKeys, handleConfigurationChange, configFill],
  );

  // Step configuration - only show step 2 if there are inputSpecs
  const steps: IStepConfig[] = useMemo(() => {
    const baseSteps: IStepConfig[] = [
      {
        number: 1,
        title: t('pollDialog.steps.configure.title'),
        description: t('pollDialog.steps.configure.description'),
      },
    ];

    if (hasInputSpecs) {
      baseSteps.push({
        number: 2,
        title: t('pollDialog.steps.mapInputs.title'),
        description: t('pollDialog.steps.mapInputs.description'),
      });
    }

    return baseSteps;
  }, [hasInputSpecs, t]);

  /**
   * Handles navigation to the previous step
   */
  const handleBack = () => {
    if (currentStep > 1) {
      onStepChange(currentStep - 1);
    }
  };

  /**
   * Gets the current form data
   */
  const getFormData = useCallback(() => {
    return {
      selectedIntegration,
      selectedIntegrationVersion,
      selectedConnection,
      selectedPollId,
      selectedPollIntervalMinutes,
      inputMappings,
      configurationValues,
    };
  }, [
    selectedIntegration,
    selectedIntegrationVersion,
    selectedConnection,
    selectedPollId,
    selectedPollIntervalMinutes,
    inputMappings,
    configurationValues,
  ]);

  const connectionGate = useInactiveConnectionGate({
    selectedIntegration,
    selectedConnection,
    enabled: isOpen && mode !== 'readOnly',
  });
  const { isPreparingConnection, runWithActiveConnection } = connectionGate;

  /**
   * Advances to the next step, or saves/updates if on the last step
   */
  const proceed = () => {
    // If no inputSpecs, save directly from step 1
    if (!hasInputSpecs && currentStep === 1) {
      const formData = getFormData();

      if (mode === 'edit') {
        if (hasAnyChanges) {
          onUpdate?.(formData);
        } else {
          // No changes - just close
          onOpenChange(false);
        }
      } else {
        // Create mode
        onSave?.(formData);
      }
      return;
    }

    // Multi-step flow (has inputSpecs)
    if (currentStep < steps.length) {
      onStepChange(currentStep + 1);
    } else {
      // Last step - save or update based on mode
      const formData = getFormData();

      if (mode === 'edit') {
        if (hasAnyChanges) {
          onUpdate?.(formData);
        } else {
          // No changes - just close
          onOpenChange(false);
        }
      } else {
        // Create mode
        onSave?.(formData);
      }
    }
  };

  /**
   * Handles the primary action: reactivates an inactive connection when needed,
   * then advances or saves
   */
  const handleNext = () => {
    void runWithActiveConnection(proceed, { waitForReady: isLastStep });
  };

  /**
   * Handles dialog open state changes
   * Prevents closing when loading
   */
  const handleOpenChange = (open: boolean) => {
    if (isLoading) return;
    onOpenChange(open);
  };

  const isLastStep = currentStep === steps.length;
  const isFirstStep = currentStep === 1;
  const isReadOnlyMode = mode === 'readOnly';
  const isEditMode = mode === 'edit';

  /**
   * Renders the current step content
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <ConfigureStep
            selectedIntegration={selectedIntegration}
            onIntegrationChange={onSelectedIntegrationChange}
            onIntegrationVersionChange={onSelectedIntegrationVersionChange}
            selectedConnection={selectedConnection}
            onConnectionChange={onSelectedConnectionChange}
            selectedPollId={selectedPollId}
            onSelectedPollChange={onSelectedPollChange}
            selectedPollIntervalMinutes={selectedPollIntervalMinutes}
            onSelectedPollIntervalMinutesChange={
              onSelectedPollIntervalMinutesChange
            }
            configurationValues={configurationValues}
            onConfigurationChange={handleConfigurationChange}
            onTriggerFieldGroupsExtracted={onTriggerFieldGroupsChange}
            onSelectedIntegrationDisplayNameChange={
              setSelectedIntegrationDisplayName
            }
            onFillableConfigKeysChange={setFillableConfigKeys}
            highlightedConfigKey={configFill.token}
            highlightConfigNonce={configFill.nonce}
            hasNextStep={hasInputSpecs}
            // TODO: Remove isEditMode once we have support from BDK
            readOnly={isReadOnlyMode || isEditMode || isPreparingConnection}
          />
        );
      case 2:
        // Only render step 2 if there are inputSpecs
        if (!hasInputSpecs) {
          return null;
        }
        return (
          <MapInputsStep
            inputSpecs={inputSpecs}
            mappings={inputMappings}
            triggerFieldGroups={triggerFieldGroups}
            onMappingChange={handleMappingChange}
            readOnly={isReadOnlyMode || isPreparingConnection}
          />
        );
      default:
        return null;
    }
  };

  // Button label derived from utility function
  const defaultPrimaryButtonLabel = getPrimaryButtonLabel({
    mode,
    isLastStep,
    hasAnyChanges,
    hasConfigureChanges,
    hasInputSpecs,
    isLoading,
    translations: {
      updateTrigger: t('trigger.actions.updateTrigger'),
      done: t('trigger.actions.done'),
      saveAndNext: t('trigger.actions.saveAndNext'),
      next: t('trigger.actions.next'),
      saveTrigger: t('trigger.actions.saveTrigger'),
      saving: t('trigger.actions.saving'),
      updating: t('trigger.actions.updating'),
    },
  });

  const { label: primaryButtonLabel, showPowerIcon } = getConnectionAction(
    connectionGate,
    {
      waiting: t('trigger.actions.waitingForConnection'),
      reactivate: t(
        isLastStep
          ? 'trigger.actions.reactivateAndSave'
          : 'trigger.actions.reactivateAndContinue',
      ),
      default: defaultPrimaryButtonLabel,
    },
  );

  // Button variant derived from utility function
  const primaryButtonVariant = getPrimaryButtonVariant({
    mode,
    isLastStep,
    hasAnyChanges,
    hasConfigureChanges,
  });

  // Dialog title derived from utility function
  const dialogTitle = getDialogTitle({
    mode,
    triggerName,
    translations: {
      viewTitle: t('pollDialog.viewTitle', { triggerName }),
      editTitle: t('pollDialog.editTitle', { triggerName }),
      createTitle: t('pollDialog.title'),
    },
  });

  // The dialog body (header + stepper + step content). Rendered as the plain
  // direct children of DialogContent when there's no assistant (identical to
  // the stock dialog), or hosted inside the assistant layout wrapper when
  // `canAssist` is true.
  const dialogBody = (
    <>
      {/* Header */}
      <DialogHeader className="gap-1">
        <Flex align="center" gap={2}>
          <DialogTitle className="text-xl font-medium">
            {dialogTitle}
          </DialogTitle>
          {triggerStatus && <TriggerStatusBadge status={triggerStatus} />}
        </Flex>
        <DialogDescription className="text-sm">
          {hasInputSpecs
            ? t('pollDialog.description')
            : t('pollDialog.noInputsDescription')}
        </DialogDescription>
      </DialogHeader>

      {/* Stepper - only show if there are multiple steps */}
      {hasInputSpecs && (
        <DialogStepper steps={steps} currentStep={currentStep} />
      )}

      {/* Step Content */}
      <Flex direction="column" className="flex-1 overflow-y-auto min-h-0">
        {renderStepContent()}
      </Flex>
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`p-5 gap-5 ${
          canAssist
            ? TRIGGER_DIALOG_MAX_WIDTH_CLASS.withAssistant
            : TRIGGER_DIALOG_MAX_WIDTH_CLASS.default
        } max-h-[calc(100vh-100px)] flex flex-col transition-[max-width] duration-300`}
        showCloseButton={!isLoading}
      >
        <TriggerAssistantDialogLayout
          panel={
            canAssist ? (
              <IntegrationAssistantPanel
                integrationName={selectedIntegration ?? ''}
                integrationDisplayName={
                  selectedIntegrationDisplayName ?? selectedIntegration ?? ''
                }
                integrationVersion={selectedIntegrationVersion ?? ''}
                organizationId={organizationId}
                workspaceId={workspaceId}
                selectedAuthId={undefined}
                connectionId={selectedConnection}
                mode="trigger"
                triggerId={selectedPollId}
                onFillForm={
                  isReadOnlyMode || isEditMode
                    ? undefined
                    : handleAssistantFillForm
                }
                isOpen
                side="left"
                closable={false}
              />
            ) : null
          }
        >
          {dialogBody}
        </TriggerAssistantDialogLayout>

        {/* Footer */}
        <DialogFooter
          className={
            onToggleActiveState ? 'sm:justify-between' : 'sm:justify-end'
          }
        >
          {onToggleActiveState && (
            <Button
              variant="secondary"
              onClick={onToggleActiveState}
              disabled={
                isToggleActiveStateLoading ||
                triggerStatus === TRIGGER_STATUS.ERROR ||
                triggerStatus === TRIGGER_STATUS.PENDING
              }
              isLoading={isToggleActiveStateLoading}
            >
              {triggerStatus === TRIGGER_STATUS.ACTIVE ? (
                <Icon size="md" type="Pause" />
              ) : (
                <Icon size="md" type="Play" />
              )}
              {triggerStatus === TRIGGER_STATUS.ACTIVE
                ? t('trigger.actions.pause')
                : t('trigger.actions.resume')}
            </Button>
          )}
          {isReadOnlyMode ? (
            /* Read-only mode - just show navigation and Close button */
            <Flex gap={2}>
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={isFirstStep}
              >
                {t('trigger.actions.back')}
              </Button>
              {isLastStep ? (
                <Button variant="default" onClick={() => onOpenChange(false)}>
                  {t('trigger.actions.close')}
                </Button>
              ) : (
                <Button variant="default" onClick={handleNext}>
                  {t('trigger.actions.next')}
                </Button>
              )}
            </Flex>
          ) : (
            /* Create/Edit mode - Back and Primary buttons */
            <Flex gap={2}>
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={isFirstStep || isLoading}
              >
                {t('trigger.actions.back')}
              </Button>
              <Button
                variant={primaryButtonVariant}
                onClick={handleNext}
                disabled={
                  isLoading ||
                  isPreparingConnection ||
                  (currentStep === 1 && !isStep1Valid)
                }
                isLoading={isLoading || isPreparingConnection}
              >
                {showPowerIcon && <Icon size="md" type="Power" />}
                {primaryButtonLabel}
              </Button>
            </Flex>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
